// apps/client/src/components/common/ErrorBoundary.tsx
// Production-grade error boundary with fallback UI
// Jeff Dean Approach: Graceful degradation, detailed logging, user-friendly errors

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (_error: Error, _errorInfo: ErrorInfo) => void;
  context?: string; // e.g., "OfficeDashboard", "StatCards"
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in child component tree,
 * logs errors, and displays fallback UI instead of crashing the app.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary context="Dashboard" fallback={<div>Failed to load dashboard</div>}>
 *   <DashboardComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    const { context, onError } = this.props;

    console.error(`[ErrorBoundary${context ? ` - ${context}` : ''}] Caught error:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Error tracking integration deliberately deferred — no remote service
    // is wired (no @sentry, no Datadog RUM, no equivalent). All client errors
    // currently surface to console + the ErrorFallback UI only. If error
    // tracking is added later, update docs/compliance/SUBPROCESSOR-REGISTER.md
    // §6 + firebase.json CSP connect-src + this comment together.
    // reportError({ error, errorInfo, context });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, context } = this.props;

    if (hasError) {
      // Custom fallback provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="p-8 bg-red-900/20 border border-red-500 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {context ? `${context} Error` : 'Something went wrong'}
              </h3>
              <p className="text-gray-300 mb-4">
                We encountered an unexpected error. The error has been logged and our team will investigate.
              </p>
              {error && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300">
                    Technical Details
                  </summary>
                  <div className="mt-2 p-3 bg-gray-900 rounded border border-gray-700">
                    <p className="text-xs text-red-400 font-mono break-all">
                      {error.message}
                    </p>
                  </div>
                </details>
              )}
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
